import * as API from '@/API';
import { SaveCustomWorklistToDashboardDTO } from '@/Models/API/WorklistsDashboard/SaveCustomWorklistToDashboardDTO';
import { WorklistsDashboardDataForUserDTO } from '@/Models/API/WorklistsDashboard/WorklistsDashboardDataForUserDTO';
import { Entity, EntityLoading, EntityNotLoaded, createLoadedEntity } from '@/Models/Entity';
import { makeAutoObservable } from 'mobx';

export type WorklistsDashboardState = WorklistsDashboardDataForUserDTO;

export interface IWorklistsDashboardStore {
    readonly state: Entity<WorklistsDashboardState>;
    deleteCustomWorklistFromDashboard: (customWorklistID: number) => Promise<void>;
    refresh: () => Promise<void>;
    saveCustomWorklistToDashboard: (worklistToDashboardSaveData: SaveCustomWorklistToDashboardDTO) => Promise<void>;
}

const DEFAULTPOLLINTERVAL = 10000;
const DEFAULTFETCHTIMEOUT = 9000;
export const MAX_WORKLISTS_IN_DASHBOARD = 10;

export class WorklistsDashboardStore implements IWorklistsDashboardStore {
    private _disposed = false;
    private _timeoutId: number | null = null;
    private _state: Entity<WorklistsDashboardState>;

    constructor() {
        makeAutoObservable(this);
        this._state = EntityNotLoaded.instance;
    }

    get state(): Entity<WorklistsDashboardState> {
        return this._state;
    }

    deleteCustomWorklistFromDashboard = async (customWorklistID: number): Promise<void> => {
        const dashboardData = await API.worklistDashboard.deleteCustomWorklistFromUserDashboard(customWorklistID);
        this.setState(dashboardData);
    };

    refresh = async (): Promise<void> => {
        await this.poll();
    };

    saveCustomWorklistToDashboard = async (worklistToDashboardSaveData: SaveCustomWorklistToDashboardDTO): Promise<void> => {
        const dashboardData = await API.worklistDashboard.saveCustomWorklistToUserDashboard(worklistToDashboardSaveData);
        this.setState(dashboardData);
    };

    initialize = async (): Promise<void> => {
        this._disposed = false;
        this._state = EntityLoading.instance;
        await this.poll(); // Start the polling mechanism
    };

    destroy = (): void => {
        this._disposed = true;
        if (this._timeoutId !== null) {
            clearTimeout(this._timeoutId);
            this._timeoutId = null;
        }
    };

    private readonly poll = async (): Promise<void> => {
        if (this._disposed) {
            return;
        }

        // In case this was called manually instead of through the timeout mechanism, clear any existing timeout to avoid multiple concurrent executions
        if (this._timeoutId !== null) {
            clearTimeout(this._timeoutId);
            this._timeoutId = null;
        }

        let nextTimeout = DEFAULTPOLLINTERVAL;
        try {
            nextTimeout = await this.fetchLatestWorklistDashboardDataForUser();
        }
        catch (err) {
            console.log(`⚠️ Failed to fetch the latest worklist dashboard info`);
            console.log(err);
        }
        finally {
            if (!this._disposed) {
                this._timeoutId = window.setTimeout(() => this.poll(), nextTimeout);
            }
        }
    };

    private readonly fetchLatestWorklistDashboardDataForUser = async (): Promise<number> => {
        const requestInit = { signal: AbortSignal.timeout(DEFAULTFETCHTIMEOUT) } satisfies RequestInit;
        const dashboardData = await API.worklistDashboard.getWorklistDashboardDataForCurrentUser(requestInit);

        this.setState(dashboardData);
        return dashboardData.RefreshTimeMs;
    };

    private readonly setState = (stateData: WorklistsDashboardDataForUserDTO): void => {
        this._state = createLoadedEntity(stateData);
    };
}
